import {
  Alert,
  AlertIcon,
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  useColorModeValue,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import { DashboardFunnelSection } from '@/components/dashboard/DashboardFunnelSection'
import { DashboardHealthSection } from '@/components/dashboard/DashboardHealthSection'
import { DashboardOverviewSection } from '@/components/dashboard/DashboardOverviewSection'
import { DashboardRecentActivitySection } from '@/components/dashboard/DashboardRecentActivitySection'
import { DashboardTodoSection } from '@/components/dashboard/DashboardTodoSection'
import { DashboardWidgetAttributionSection } from '@/components/dashboard/DashboardWidgetAttributionSection'
import type {
  DashboardFunnelStat,
  DashboardHealthItem,
  DashboardStat,
  DashboardTodoItem,
} from '@/components/dashboard/types'
import { useDashboardMetrics } from '@/hooks/useAdminData'
import { formatDateTime, formatNumber } from '@/utils/formatUtils'
import { useDeferredNavigation } from '@/navigation/DeferredNavigationProvider'

export function DashboardPage() {
  const { t } = useTranslation()
  const { navigateDeferred } = useDeferredNavigation()
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const subtitleColor = useColorModeValue('gray.600', 'gray.300')
  const statNumberColor = useColorModeValue('gray.800', 'white')
  const borderColor = useColorModeValue('gray.100', 'gray.600')
  const overviewSubCardBg = useColorModeValue('gray.50', 'gray.650')
  const overviewSubCardBorderColor = useColorModeValue('gray.200', 'gray.500')
  const rowHoverBg = useColorModeValue('gray.50', 'gray.650')
  const sectionSpacing = { base: 4, md: 6 }
  const { data: metrics, isLoading, error } = useDashboardMetrics()

  const formatMetricValue = (value: number | null | undefined) => (
    value == null ? t('common.na') : formatNumber(value)
  )

  const formatMetricAge = (value: number | null | undefined) => (
    value == null
      ? t('common.na')
      : t('dashboard.health.secondsAgo', { count: value })
  )

  const formatMetricPercentage = (value: number | null | undefined) => (
    value == null ? t('common.na') : `${formatNumber(value, 2)}%`
  )

  const formatMetricCurrencyValue = (value: number | null | undefined) => (
    value == null ? t('common.na') : formatNumber(value, 2)
  )

  const stats: DashboardStat[] = [
    {
      label: t('dashboard.metrics.users'),
      items: [
        {
          label: t('dashboard.metrics.total'),
          value: formatMetricValue(metrics?.overview.totalUsers),
        },
        {
          label: t('dashboard.metrics.active'),
          value: formatMetricValue(metrics?.overview.activeUsers),
        },
      ],
    },
    {
      label: t('dashboard.metrics.merchants'),
      items: [
        {
          label: t('dashboard.metrics.total'),
          value: formatMetricValue(metrics?.overview.totalMerchants),
        },
        {
          label: t('dashboard.metrics.active'),
          value: formatMetricValue(metrics?.overview.activeMerchants),
        },
      ],
    },
    {
      label: t('dashboard.metrics.orders'),
      items: [
        {
          label: t('dashboard.metrics.total'),
          value: formatMetricValue(metrics?.overview.totalOrders),
        },
        {
          label: t('dashboard.metrics.active'),
          value: formatMetricValue(metrics?.overview.activeOrders),
        },
        {
          label: t('dashboard.metrics.settled'),
          value: formatMetricValue(metrics?.overview.settledOrders),
        },
      ],
    },
    {
      label: `${t('dashboard.metrics.orderVolume')} ($)`,
      items: [
        {
          label: t('dashboard.metrics.paid'),
          value: formatMetricCurrencyValue(metrics?.overview.paymentVolumeUsd),
        },
        {
          label: t('dashboard.metrics.settled'),
          value: formatMetricCurrencyValue(metrics?.overview.settledVolumeUsd),
        },
      ],
    },
    {
      label: `${t('dashboard.metrics.platformFeeRevenue')} ($)`,
      value: formatMetricCurrencyValue(metrics?.overview.platformFeeRevenueUsd),
    },
    {
      label: t('dashboard.metrics.failedOperationsLastHour'),
      value: formatMetricValue(metrics?.health.failedOperationsLastHour),
    },
    {
      label: t('dashboard.metrics.contracts'),
      items: [
        {
          label: t('dashboard.metrics.deployed'),
          value: formatMetricValue(metrics?.overview.deployedContracts),
        },
        {
          label: t('dashboard.metrics.pending'),
          value: formatMetricValue(metrics?.todo.pendingContractDeployments),
        },
      ],
    },
    {
      label: t('dashboard.metrics.settlements'),
      items: [
        {
          label: t('dashboard.metrics.settled'),
          value: formatMetricValue(metrics?.overview.settledOrders),
        },
        {
          label: t('dashboard.metrics.pending'),
          value: formatMetricValue(metrics?.todo.pendingSettlementVerifications),
        },
      ],
    },
  ]

  const todoRows: DashboardTodoItem[] = [
    {
      label: t('dashboard.todo.pendingMerchantReviews'),
      value: metrics?.todo.pendingMerchantReviews,
      path: '/merchants',
    },
    {
      label: t('dashboard.todo.pendingContractDeployments'),
      value: metrics?.todo.pendingContractDeployments,
      path: '/contract-management',
    },
    {
      label: t('dashboard.todo.pendingSettlementVerifications'),
      value: metrics?.todo.pendingSettlementVerifications,
      path: '/payment-management',
    },
    {
      label: t('dashboard.todo.pendingRefundVerifications'),
      value: metrics?.todo.pendingRefundVerifications,
      path: '/payment-management',
    },
    {
      label: t('dashboard.todo.pendingRescueTasks'),
      value: metrics?.todo.pendingRescueTasks,
      path: '/rescue',
    },
  ]

  const healthRows: DashboardHealthItem[] = [
    {
      label: t('dashboard.health.webhookPendingCount'),
      value: formatMetricValue(metrics?.health.webhookPendingCount),
    },
    {
      label: t('dashboard.health.webhookRetryScheduledCount'),
      value: formatMetricValue(metrics?.health.webhookRetryScheduledCount),
    },
    {
      label: t('dashboard.health.gasPriceAge'),
      value: formatMetricAge(metrics?.health.gasPriceAgeSeconds),
    },
    {
      label: t('dashboard.health.economyParamsAge'),
      value: formatMetricAge(metrics?.health.economyParamsAgeSeconds),
    },
  ]

  const funnelStats: DashboardFunnelStat[] = [
    {
      label: t('dashboard.funnel.createdOrdersLast24h'),
      value: formatMetricValue(metrics?.funnel.createdOrdersLast24h),
    },
    {
      label: t('dashboard.funnel.paidOrdersLast24h'),
      value: formatMetricValue(metrics?.funnel.paidOrdersLast24h),
    },
    {
      label: t('dashboard.funnel.settledOrdersLast24h'),
      value: formatMetricValue(metrics?.funnel.settledOrdersLast24h),
    },
    {
      label: t('dashboard.funnel.expiredOrdersLast24h'),
      value: formatMetricValue(metrics?.funnel.expiredOrdersLast24h),
    },
    {
      label: t('dashboard.funnel.refundedOrdersLast24h'),
      value: formatMetricValue(metrics?.funnel.refundedOrdersLast24h),
    },
    {
      label: t('dashboard.funnel.platformFeeUsdLast24h'),
      value: formatMetricCurrencyValue(metrics?.funnel.platformFeeUsdLast24h),
    },
    {
      label: t('dashboard.funnel.paymentRate'),
      value: formatMetricPercentage(metrics?.funnel.paymentRate),
    },
    {
      label: t('dashboard.funnel.settlementRate'),
      value: formatMetricPercentage(metrics?.funnel.settlementRate),
    },
  ]

  const widgetAttribution = metrics?.widgetAttribution
  const initialModeBreakdown = widgetAttribution?.initialModeBreakdown
  const lastModeBreakdown = widgetAttribution?.lastModeBreakdown

  const widgetSummaryStats: DashboardFunnelStat[] = [
    {
      label: t('dashboard.widgetAttribution.trackedOrders'),
      value: formatMetricValue(widgetAttribution?.trackedOrders),
    },
    {
      label: t('dashboard.widgetAttribution.trackedOrdersLast24h'),
      value: formatMetricValue(widgetAttribution?.trackedOrdersLast24h),
    },
    {
      label: t('dashboard.widgetAttribution.fallbackCount'),
      value: formatMetricValue(widgetAttribution?.fallbackCount),
    },
    {
      label: t('dashboard.widgetAttribution.fallbackRate'),
      value: formatMetricPercentage(widgetAttribution?.fallbackRate),
    },
  ]

  const widgetInitialModeStats: DashboardFunnelStat[] = [
    {
      label: t('dashboard.widgetAttribution.modes.redirect'),
      value: formatMetricValue(initialModeBreakdown?.redirect),
    },
    {
      label: t('dashboard.widgetAttribution.modes.modal'),
      value: formatMetricValue(initialModeBreakdown?.modal),
    },
    {
      label: t('dashboard.widgetAttribution.modes.inline'),
      value: formatMetricValue(initialModeBreakdown?.inline),
    },
    {
      label: t('dashboard.widgetAttribution.modes.lite'),
      value: formatMetricValue(initialModeBreakdown?.lite),
    },
  ]

  const widgetLastModeStats: DashboardFunnelStat[] = [
    {
      label: t('dashboard.widgetAttribution.modes.redirect'),
      value: formatMetricValue(lastModeBreakdown?.redirect),
    },
    {
      label: t('dashboard.widgetAttribution.modes.modal'),
      value: formatMetricValue(lastModeBreakdown?.modal),
    },
    {
      label: t('dashboard.widgetAttribution.modes.inline'),
      value: formatMetricValue(lastModeBreakdown?.inline),
    },
    {
      label: t('dashboard.widgetAttribution.modes.lite'),
      value: formatMetricValue(lastModeBreakdown?.lite),
    },
  ]

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'healthy':
        return 'green'
      case 'degraded':
        return 'yellow'
      case 'unhealthy':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <Box bg={pageBg} minH="calc(100dvh - 3.5rem)" pb={{ base: 4, md: 6, lg: 8 }}>
      <Heading size="lg" mb={2}>
        {t('dashboard.title')}
      </Heading>
      {metrics?.updatedAt && !isLoading && !error && (
        <Text color={subtitleColor} fontSize="sm" mb={sectionSpacing}>
          {t('dashboard.updatedAt', {
            value: formatDateTime(metrics.updatedAt),
          })}
        </Text>
      )}
      {!metrics?.updatedAt || isLoading || error ? <Box mb={sectionSpacing} /> : null}

      {isLoading && (
        <Box role="status" aria-busy="true">
          <PageSkeleton minHeight={400} showHeader={false} />
          <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
        </Box>
      )}

      {error && (
        <Alert status="error">
          <AlertIcon />
          {t('dashboard.errors.loadMetrics', { error: t('common.error') })}
        </Alert>
      )}

      {!isLoading && !error && metrics && (
        <VStack align="stretch" spacing={sectionSpacing}>
          <DashboardOverviewSection
            stats={stats}
            cardBg={cardBg}
            subCardBg={overviewSubCardBg}
            subCardBorderColor={overviewSubCardBorderColor}
            subtitleColor={subtitleColor}
            statNumberColor={statNumberColor}
          />

          <DashboardFunnelSection
            title={t('dashboard.funnel.title')}
            items={funnelStats}
            cardBg={cardBg}
            textColor={textColor}
            subtitleColor={subtitleColor}
            statNumberColor={statNumberColor}
            borderColor={borderColor}
          />

          <DashboardWidgetAttributionSection
            title={t('dashboard.widgetAttribution.title')}
            summaryTitle={t('dashboard.widgetAttribution.summary')}
            initialTitle={t('dashboard.widgetAttribution.initialModes')}
            lastTitle={t('dashboard.widgetAttribution.lastModes')}
            summaryItems={widgetSummaryStats}
            initialItems={widgetInitialModeStats}
            lastItems={widgetLastModeStats}
            cardBg={cardBg}
            textColor={textColor}
            subtitleColor={subtitleColor}
            statNumberColor={statNumberColor}
            borderColor={borderColor}
          />

          <Grid
            templateColumns={{
              base: '1fr',
              md: '1fr',
              lg: '2fr 1fr'
            }}
            gap={sectionSpacing}
          >
            <GridItem>
              <DashboardRecentActivitySection
                title={t('dashboard.recentActivity')}
                viewAllLabel={t('dashboard.viewAll')}
                emptyLabel={t('dashboard.activity.empty')}
                systemEventLabel={t('dashboard.activity.systemEvent')}
                items={metrics.recentActivity}
                cardBg={cardBg}
                textColor={textColor}
                subtitleColor={subtitleColor}
                borderColor={borderColor}
                onViewAll={() => navigateDeferred('/audit')}
              />
            </GridItem>

            <GridItem>
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                <DashboardTodoSection
                  title={t('dashboard.todo.title')}
                  items={todoRows}
                  cardBg={cardBg}
                  textColor={textColor}
                  subtitleColor={subtitleColor}
                  borderColor={borderColor}
                  hoverBg={rowHoverBg}
                  onNavigate={navigateDeferred}
                  formatValue={formatMetricValue}
                />

                <DashboardHealthSection
                  title={t('dashboard.systemStatus')}
                  statusLabel={t(`dashboard.health.status.${metrics.health.overallStatus}`)}
                  statusColor={getStatusColor(metrics.health.overallStatus)}
                  items={healthRows}
                  cardBg={cardBg}
                  textColor={textColor}
                  subtitleColor={subtitleColor}
                  borderColor={borderColor}
                  viewMonitoringLabel={t('dashboard.viewMonitoring')}
                  onOpenMonitoring={() => navigateDeferred('/monitoring')}
                />
              </VStack>
            </GridItem>
          </Grid>
        </VStack>
      )}
    </Box>
  )
}

export default DashboardPage
